'use client'

import { isArray } from 'lodash'

import { logger } from '~/debug/logger'

import type { DesktopTab } from './tabsSlice'

/**
 * Tab-set persistence for the desktop shell: an operations tool should wake
 * up with the same tabs it was closed with. Restored explicitly by
 * `DesktopTitleBar` on mount (before any navigation dispatch), so there is no
 * async-rehydrate race with cold-start deep links.
 */

const STORAGE_KEY = 'synop.desktop.tabs'
const MAX_PERSISTED_TABS = 20

function isDesktopTab(value: unknown): value is DesktopTab {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record.id === 'string' &&
    typeof record.path === 'string' &&
    record.path.startsWith('/') &&
    (record.title === null || typeof record.title === 'string')
  )
}

export function readPersistedTabs(): DesktopTab[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    const parsed: unknown = JSON.parse(stored)
    if (!isArray(parsed)) return []
    return parsed.filter(isDesktopTab).slice(0, MAX_PERSISTED_TABS)
  } catch (error) {
    logger.warn('Failed to read persisted desktop tabs', error)
    return []
  }
}

export function writePersistedTabs(tabs: DesktopTab[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.slice(0, MAX_PERSISTED_TABS)))
  } catch (error) {
    logger.warn('Failed to persist desktop tabs', error)
  }
}
